/**
 * InboxModal — B1: Admin Ingestion Inbox
 * Slide-in panel showing pending ingest_queue items with Ingest/Dismiss/Queue actions.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface QueueItem {
  id: string;
  url: string;
  standard_name: string;
  skill: string;
  source_domain: string;
  source_type: string;
  status: string;
  clauses_inserted: number | null;
  vectors_inserted: number | null;
  error_message: string | null;
  retry_count: number;
  requested_by: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  issue_id: string | null;
}

interface InboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when badge count changes */
  onCountChange?: (count: number) => void;
}

async function fetchQueueItems(statusFilter?: string): Promise<QueueItem[]> {
  const params = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  const res = await authFetch(`${API_BASE}/api/admin/ingestion-queue${params}`);
  if (!res.ok) throw new Error(`Queue fetch failed (${res.status})`);
  const data = await res.json() as { queue: QueueItem[] };
  return data.queue;
}

async function applyAction(id: string, action: 'approve' | 'dismiss' | 'requeue'): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/admin/ingestion-queue/${encodeURIComponent(id)}/action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' })) as { message?: string };
    throw new Error(err.message ?? `Action failed (${res.status})`);
  }
}

export async function fetchIngestionQueueCount(): Promise<number> {
  try {
    const res = await authFetch(`${API_BASE}/api/admin/ingestion-queue/count`);
    if (!res.ok) return 0;
    const data = await res.json() as { count: number };
    return data.count ?? 0;
  } catch {
    return 0;
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'pending':
    case 'auto-queued': return 'iq-badge iq-badge--pending';
    case 'processing': return 'iq-badge iq-badge--processing';
    case 'completed': return 'iq-badge iq-badge--done';
    case 'failed': return 'iq-badge iq-badge--failed';
    case 'dismissed': return 'iq-badge iq-badge--dismissed';
    default: return 'iq-badge';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

type FilterStatus = 'pending' | 'all' | 'completed' | 'failed';

export default function InboxModal({ isOpen, onClose, onCountChange }: InboxModalProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusParam = filter === 'all' ? undefined : filter;
      const data = await fetchQueueItems(statusParam);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isOpen) {
      void load();
    }
  }, [isOpen, load]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  const handleAction = async (id: string, action: 'approve' | 'dismiss' | 'requeue') => {
    setActionLoading(id + ':' + action);
    setActionError(null);
    try {
      await applyAction(id, action);
      await load();
      // Refresh badge count
      if (onCountChange) {
        const count = await fetchIngestionQueueCount();
        onCountChange(count);
      }
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const pendingItems = items.filter(i => i.status === 'pending' || i.status === 'auto-queued');
  const isAutoQueued = (item: QueueItem) => item.status === 'auto-queued';

  if (!isOpen) return null;

  return (
    <>
      <div className="iq-overlay" aria-hidden="true" />
      <div
        ref={panelRef}
        className="iq-panel"
        role="dialog"
        aria-label="Ingestion Inbox"
        aria-modal="true"
      >
        {/* Header */}
        <div className="iq-header">
          <div className="iq-header-title">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
              <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
            Ingestion Inbox
            {pendingItems.length > 0 && (
              <span className="iq-header-badge">{pendingItems.length}</span>
            )}
          </div>
          <div className="iq-header-actions">
            <button className="iq-refresh-btn" onClick={() => void load()} title="Refresh" aria-label="Refresh">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </button>
            <button className="iq-close-btn" onClick={onClose} aria-label="Close inbox">✕</button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="iq-filters">
          {(['pending', 'all', 'completed', 'failed'] as FilterStatus[]).map(f => (
            <button
              key={f}
              className={`iq-filter-btn${filter === f ? ' iq-filter-btn--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'pending' ? 'Pending' : f === 'all' ? 'All' : f === 'completed' ? 'Done' : 'Failed'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="iq-content">
          {loading && (
            <div className="iq-loading">Loading queue…</div>
          )}
          {!loading && error && (
            <div className="iq-error">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="iq-empty">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
              <p>No items in this view</p>
            </div>
          )}
          {actionError && (
            <div className="iq-action-error">{actionError}</div>
          )}
          {!loading && items.map(item => (
            <div key={item.id} className="iq-item">
              <div className="iq-item-header">
                <span className="iq-item-name" title={item.standard_name}>
                  {item.standard_name}
                  {isAutoQueued(item) && (
                    <span className="iq-auto-tag">Auto-detected</span>
                  )}
                </span>
                <span className={statusBadgeClass(item.status)}>{item.status}</span>
              </div>
              <div className="iq-item-meta">
                <span className="iq-item-domain">{item.source_domain}</span>
                <span className="iq-item-dot">·</span>
                <span className="iq-item-skill">{item.skill}</span>
                <span className="iq-item-dot">·</span>
                <span className="iq-item-date">{formatDate(item.created_at)}</span>
              </div>
              <div className="iq-item-url" title={item.url}>
                <a href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</a>
              </div>
              {item.error_message && (
                <div className="iq-item-error">{item.error_message}</div>
              )}
              {(item.status === 'pending' || item.status === 'auto-queued' || item.status === 'failed') && (
                <div className="iq-item-actions">
                  <button
                    className="iq-action-btn iq-action-btn--approve"
                    onClick={() => void handleAction(item.id, 'approve')}
                    disabled={actionLoading === item.id + ':approve'}
                    title="Approve for ingestion"
                  >
                    {actionLoading === item.id + ':approve' ? '…' : '✓ Ingest'}
                  </button>
                  <button
                    className="iq-action-btn iq-action-btn--requeue"
                    onClick={() => void handleAction(item.id, 'requeue')}
                    disabled={actionLoading === item.id + ':requeue'}
                    title="Re-queue item"
                  >
                    {actionLoading === item.id + ':requeue' ? '…' : '↺ Queue'}
                  </button>
                  <button
                    className="iq-action-btn iq-action-btn--dismiss"
                    onClick={() => void handleAction(item.id, 'dismiss')}
                    disabled={actionLoading === item.id + ':dismiss'}
                    title="Dismiss this item"
                  >
                    {actionLoading === item.id + ':dismiss' ? '…' : '✕ Dismiss'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
